import SEO from "../../components/SEO";
import { Link } from "react-router-dom";

export default function SecurityPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "セキュリティ - dot Blue",
    "description": "dot Blue株式会社のセキュリティ対策について説明しています。",
    "url": `${siteUrl}/security`,
    "publisher": {
      "@type": "Organization",
      "name": "dot Blue",
      "url": siteUrl
    }
  };

  return (
    <>
      <SEO
        title="セキュリティ - dot Blue"
        description="dot Blue株式会社のセキュリティ対策について説明しています。安全なサービス提供のための取り組みをご紹介します。"
        keywords="セキュリティ, 情報セキュリティ, dot Blue"
        ogImage={`${siteUrl}/og-image-security.jpg`}
        schema={webPageSchema}
      />

      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                <span className="text-xl font-bold text-gray-900">dot Blue</span>
              </Link>
              <Link
                to="/"
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                ホームに戻る
              </Link>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="pt-24 pb-20">
          <div className="max-w-4xl mx-auto px-6">
            <h1 className="text-4xl font-bold text-gray-900 mb-8">情報セキュリティ基本方針</h1>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-8">
                株式会社dot Blue（以下「当社」）は、当社が取り扱う情報資産（顧客情報、業務情報、採用関連情報等）を適切に保護し、信頼されるサービス提供を行うため、以下の方針に基づき情報セキュリティ対策を実施します。
              </p>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-600 pb-2">1. 適用範囲</h2>
                <p className="text-gray-700 leading-relaxed">
                  本方針は、当社ウェブサイト（aidotblue.com）および当社が提供する法人向けサービスにおける情報資産の取り扱いに適用されます。
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-600 pb-2">2. 体制</h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  当社は、情報セキュリティを重要な経営課題として位置づけ、代表取締役の責任のもと、合理的な安全管理措置を講じます。
                </p>
                <div className="bg-gray-50 rounded-lg p-6">
                  <p className="text-gray-700">
                    <strong>情報セキュリティに関するお問い合わせ窓口：</strong>
                    <a href="mailto:contact@aidotblue.com" className="text-blue-600 hover:underline ml-2">
                      contact@aidotblue.com
                    </a>
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-600 pb-2">3. 情報資産の分類</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  当社は、取り扱う情報を以下の区分で整理し、区分に応じた管理を行います。
                </p>
                <div className="space-y-4">
                  <div className="bg-blue-50 rounded-lg p-5">
                    <p className="font-semibold text-gray-900 mb-2">公開情報</p>
                    <p className="text-gray-700">当社ウェブサイト等で公開されている情報</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-5">
                    <p className="font-semibold text-gray-900 mb-2">業務情報</p>
                    <p className="text-gray-700">提案書、設計書、対話ログ（個人情報を含まない前提のものを含む）、FAQ/ナレッジ等</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-5">
                    <p className="font-semibold text-gray-900 mb-2">機密/個人情報</p>
                    <p className="text-gray-700">氏名、メールアドレス、契約・請求情報等、機密性の高い情報</p>
                  </div>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-600 pb-2">4. 安全管理措置（概要）</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  当社は、以下の観点で安全管理措置を講じます（具体的な実装は、案件・環境により異なる場合があります）。
                </p>
                <div className="space-y-4 mb-4">
                  <div>
                    <p className="font-semibold text-gray-900 mb-2">組織的対策</p>
                    <p className="text-gray-700 pl-4">アクセス権限の管理、委託先の管理、運用ルールの整備</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-2">人的対策</p>
                    <p className="text-gray-700 pl-4">従業員・委託先との秘密保持、必要な範囲での教育</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-2">技術的対策</p>
                    <p className="text-gray-700 pl-4">アクセス制御、ログ管理、通信の保護等</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 mb-2">委託先管理</p>
                    <p className="text-gray-700 pl-4">クラウド/解析/LLM API 等の外部事業者を利用する場合、合理的な範囲で安全管理を確認し、適切な契約・運用を行います</p>
                  </div>
                </div>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                  <p className="text-sm text-gray-700">
                    ※当社は、セキュリティ上の観点から、対策の詳細を全面的に公開しない場合があります。必要に応じて、個別案件にて説明します。
                  </p>
                </div>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-600 pb-2">5. 外部サービスの利用</h2>
                <p className="text-gray-700 leading-relaxed">
                  当社は、サービス提供のため、外部サービスを利用する場合があります（例：GCP、Cloudflare、OpenAI API 等）。外部サービスの利用に際しては、当社の運用方針に基づき適切に管理します。
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-600 pb-2">6. インシデント対応</h2>
                <p className="text-gray-700 leading-relaxed">
                  情報セキュリティ上の問題（不正アクセス、情報漏えいの疑い等）が発生した場合、当社は状況把握と被害拡大防止に努め、必要に応じて関係者へ速やかに連絡します。
                </p>
              </section>

              <section className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-600 pb-2">7. 改定</h2>
                <p className="text-gray-700 leading-relaxed">
                  当社は、必要に応じて本方針を見直し、継続的な改善に努めます。
                </p>
              </section>

              <div className="border-t-2 border-gray-200 pt-6 mt-10">
                <p className="text-gray-700 font-semibold">制定日：2026年1月19日</p>
                <p className="text-gray-700 font-semibold">株式会社dot Blue</p>
              </div>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-3">
                <span className="text-lg font-bold">dot Blue</span>
              </div>
              <div className="flex gap-6 text-sm">
                <Link to="/privacy-policy" className="hover:text-blue-400 transition-colors cursor-pointer">
                  プライバシーポリシー
                </Link>
                <Link to="/terms" className="hover:text-blue-400 transition-colors cursor-pointer">
                  利用規約
                </Link>
                <Link to="/security" className="hover:text-blue-400 transition-colors cursor-pointer">
                  セキュリティ
                </Link>
                <a href="mailto:contact@aidotblue.com" className="hover:text-blue-400 transition-colors cursor-pointer">
                  お問い合わせ
                </a>
              </div>
            </div>
            <div className="text-center text-sm text-gray-400 mt-6">
              Copyright © dot Blue Inc. All Rights Reserved.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
